import type {ReactElement} from 'react';
import React, {useRef} from 'react';
import {Image} from 'react-bootstrap';
import {Text, TextSize} from '@Components/text';
import {getAssetUrl} from '@Utils/s3.util';
import {DragDropOverlay} from '@Components/drag-drop-overlay/drag-drop-overlay';
import {useAiPromptBox} from '@Components/ai-prompt-box/hooks/useAiPromptBox';
import {handleDragDropFilesUpload, AI_PROMPT_BOX_ACCEPTED_IMAGE_TYPES} from '@Components/ai-prompt-box/libraries/ai-prompt-box-drag-drop.library';
import {GROWL_TYPE, showMessageGrowl} from '@Components/message-growl';
import type {AiChatDragDropOverlayProps} from './ai-chat-drag-drop-overlay.types';
import styles from './ai-chat-drag-drop-overlay.module.scss';

export function AiChatDragDropOverlay({promptBoxId, targetElement}: AiChatDragDropOverlayProps): ReactElement {
  const {state, actions} = useAiPromptBox(promptBoxId);
  const attachmentsRef = useRef(state.attachments);

  attachmentsRef.current = state.attachments;

  const onInvalidFileTypeError = (): void => {
    showMessageGrowl({
      type: GROWL_TYPE.DANGER,
      text: window.i18next.t('pmwjs_ai_chat_unsupported_file_type'),
      showCloseIcon: true,
      interval: 7000,
    });
  };

  const onFilesUpload = (files: File[]): void => {
    handleDragDropFilesUpload(
      files,
      {
        getAttachmentCount: () => attachmentsRef.current?.items.length ?? 0,
        addLoadingAttachments: actions.addLoadingAttachments,
        removeLoadingAttachments: actions.removeLoadingAttachments,
        addAttachments: actions.addAttachments,
        removeImageAttachments: actions.removeImageAttachments,
        setActive: actions.setActiveIfNotProcessing,
      },
      onInvalidFileTypeError
    );
  };

  const hoverContent = (
    <div className={`_full-width _full-height ${styles.content}`}>
      <Image className={styles.overlayImage} src={getAssetUrl('ai-chat-drag-drop-overlay-image.webp')} alt={window.i18next.t('pmwjs_ai_chat_drag_drop_heading')} />
      <Text className={`spacing-m-t-4 content-body-white`} val={window.i18next.t('pmwjs_ai_chat_drag_drop_heading')} size={TextSize.LARGE} bold />
      <Text className="content-body-white" val={window.i18next.t('pmwjs_ai_chat_drag_drop_description')} size={TextSize.SMALL} />
    </div>
  );

  return (
    <DragDropOverlay
      onFilesUpload={onFilesUpload}
      onHoverContent={hoverContent}
      className={styles.container}
      acceptedTypes={AI_PROMPT_BOX_ACCEPTED_IMAGE_TYPES}
      onInvalidFileTypeError={onInvalidFileTypeError}
      targetElement={targetElement}
    />
  );
}
